/**
 * Admin Question Bank List
 * List all question banks with management options
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { questionBanksApi } from '../api/questionBanks.js';
import { QuestionBank, PaginatedResponse } from '../../types/models';
import { SkillsUSAUploadModal } from '../components/SkillsUSAUploadModal';
import { SkillsUSAPreviewModal } from '../components/SkillsUSAPreviewModal';
import { LoadingModal } from '../components/LoadingModal';
import { AlertModal, AlertType } from '../components/AlertModal';

interface SkillsUSAPreviewData {
  banks: Array<{
    name: string;
    external_id: string;
    questions: any[];
    question_count: number;
  }>;
  total_banks: number;
  total_questions: number;
  errors: string[];
  warnings: string[];
}

export const AdminQuestionBankList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const searchTerm = searchParams.get('search') || '';
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const setSearchTerm = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set('search', value); else params.delete('search');
    setSearchParams(params, { replace: true });
  };

  // SkillsUSA import state
  const [showSkillsUSAUploadModal, setShowSkillsUSAUploadModal] = useState(false);
  const [showSkillsUSAPreviewModal, setShowSkillsUSAPreviewModal] = useState(false);
  const [skillsUSAPreviewData, setSkillsUSAPreviewData] = useState<SkillsUSAPreviewData | null>(null);
  const [analyzingSkillsUSA, setAnalyzingSkillsUSA] = useState(false);
  const [importingSkillsUSA, setImportingSkillsUSA] = useState(false);
  const [createAssessmentFromImport, setCreateAssessmentFromImport] = useState(false);
  const [assessmentNameFromImport, setAssessmentNameFromImport] = useState('');
  const [alert, setAlert] = useState<{ message: string; type: AlertType; title?: string } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadQuestionBanks();
  }, [debouncedSearchTerm]);

  const loadQuestionBanks = async () => {
    try {
      if (questionBanks.length === 0) setLoading(true);
      const result: PaginatedResponse<QuestionBank> = await questionBanksApi.list({
        search: debouncedSearchTerm || undefined,
        limit: 100,
      });
      setQuestionBanks(result.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load question banks');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this question bank?')) {
      return;
    }
    try {
      await questionBanksApi.delete(id);
      loadQuestionBanks();
    } catch (err: any) {
      alert('Failed to delete: ' + (err.message || 'Unknown error'));
    }
  };

  const handleSkillsUSAAnalyze = async (banksFile: File, questionsFile: File, optionsFile: File, createAssessment: boolean, assessmentName: string) => {
    setCreateAssessmentFromImport(createAssessment);
    setAssessmentNameFromImport(assessmentName);
    setAnalyzingSkillsUSA(true);
    try {
      const result = await questionBanksApi.analyzeSkillsUSA(banksFile, questionsFile, optionsFile);
      setSkillsUSAPreviewData(result);
      setShowSkillsUSAUploadModal(false);
      setShowSkillsUSAPreviewModal(true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.details
        ? err.response.data.details.join('\n')
        : err.response?.data?.error || err.message || 'Failed to analyze files';
      setShowSkillsUSAUploadModal(false);
      setAlert({ message: errorMessage, type: 'error', title: 'Analysis Failed' });
    } finally {
      setAnalyzingSkillsUSA(false);
    }
  };

  const handleSkillsUSASingleFileAnalyze = async (csvFile: File, createAssessment: boolean, assessmentName: string) => {
    setCreateAssessmentFromImport(createAssessment);
    setAssessmentNameFromImport(assessmentName);
    setAnalyzingSkillsUSA(true);
    try {
      const result = await questionBanksApi.analyzeSkillsUSASingle(csvFile);
      setSkillsUSAPreviewData(result);
      setShowSkillsUSAUploadModal(false);
      setShowSkillsUSAPreviewModal(true);
    } catch (err: any) {
      const errorMessage = err.response?.data?.details
        ? err.response.data.details.join('\n')
        : err.response?.data?.error || err.message || 'Failed to analyze file';
      setShowSkillsUSAUploadModal(false);
      setAlert({ message: errorMessage, type: 'error', title: 'Analysis Failed' });
    } finally {
      setAnalyzingSkillsUSA(false);
    }
  };

  const handleSkillsUSAImport = async (banks: SkillsUSAPreviewData['banks']) => {
    setShowSkillsUSAPreviewModal(false);
    setImportingSkillsUSA(true);
    try {
      const result = await questionBanksApi.importSkillsUSA(banks, createAssessmentFromImport, assessmentNameFromImport || undefined);
      setSkillsUSAPreviewData(null);
      setCreateAssessmentFromImport(false);
      setAssessmentNameFromImport('');

      let message = `Successfully created ${result.banks_created} question bank(s) with ${result.total_questions_created} total questions.`;
      if (result.assessment_id) {
        message += `\n\nA draft assessment has been created. Go to the assessment to finish configuring it.`;
      }
      if (result.errors && result.errors.length > 0) {
        message += `\n\nWarnings:\n${result.errors.join('\n')}`;
      }
      setAlert({ message, type: 'success', title: 'Import Complete' });
      loadQuestionBanks();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to import';
      setAlert({ message: errorMessage, type: 'error', title: 'Import Failed' });
    } finally {
      setImportingSkillsUSA(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading question banks...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Question Bank Management</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setShowSkillsUSAUploadModal(true)}
            className="btn btn-secondary"
          >
            Import SkillsUSA Assessment
          </button>
          <Link to="/admin/question-banks/new" className="btn btn-primary">
            Create New Question Bank
          </Link>
        </div>
      </div>

      <div className="admin-filters">
        <label>
          Search:{' '}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search question banks..."
            style={{ marginLeft: '0.5rem', padding: '0.5rem' }}
          />
        </label>
      </div>

      <div className="assessments-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Questions</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {questionBanks.length === 0 ? (
              <tr>
                <td colSpan={5}>No question banks found.</td>
              </tr>
            ) : (
              questionBanks.map((questionBank) => (
                <tr key={questionBank.id}>
                  <td>
                    <Link to={`/admin/question-banks/${questionBank.id}`}>
                      {questionBank.name}
                    </Link>
                  </td>
                  <td>{questionBank.description || '-'}</td>
                  <td>{questionBank.question_count ?? 0}</td>
                  <td>{new Date(questionBank.created_at).toLocaleDateString()}</td>
                  <td className="actions">
                    <Link
                      to={`/admin/question-banks/${questionBank.id}`}
                      className="btn btn-small"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(questionBank.id)}
                      className="btn btn-small btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* SkillsUSA Upload Modal */}
      <SkillsUSAUploadModal
        isOpen={showSkillsUSAUploadModal}
        onClose={() => setShowSkillsUSAUploadModal(false)}
        onUploadThreeFile={handleSkillsUSAAnalyze}
        onUploadSingleFile={handleSkillsUSASingleFileAnalyze}
        isUploading={analyzingSkillsUSA}
      />

      {/* SkillsUSA Preview Modal */}
      {showSkillsUSAPreviewModal && skillsUSAPreviewData && (
        <SkillsUSAPreviewModal
          banks={skillsUSAPreviewData.banks}
          totalBanks={skillsUSAPreviewData.total_banks}
          totalQuestions={skillsUSAPreviewData.total_questions}
          errors={skillsUSAPreviewData.errors}
          warnings={skillsUSAPreviewData.warnings}
          onConfirm={handleSkillsUSAImport}
          onCancel={() => {
            setShowSkillsUSAPreviewModal(false);
            setSkillsUSAPreviewData(null);
          }}
          isImporting={importingSkillsUSA}
        />
      )}

      {/* Loading overlay during analysis or import */}
      {analyzingSkillsUSA && (
        <LoadingModal
          message="Analyzing SkillsUSA CSV files..."
          subMessage="This may take a moment"
        />
      )}
      {importingSkillsUSA && (
        <LoadingModal
          message="Importing SkillsUSA question banks..."
          subMessage="Creating banks and questions"
        />
      )}

      {/* Alert Modal */}
      {alert && (
        <AlertModal
          isOpen={true}
          onClose={() => setAlert(null)}
          title={alert.title}
          message={alert.message}
          type={alert.type}
        />
      )}
    </div>
  );
};
